import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ListChange } from '../../../../../../backend/src/tiptap/editor/extensions/marks/suggestion'

import {
  categorizeSuggestion,
  collectFormattingLabelDescriptors,
  collectListChangeLabelDescriptors,
  SuggestionCategory,
  SuggestionTextRun,
} from '../extensions/track-changes-utils'
import { SuggestionMetaComponent } from '../suggestion-meta/suggestion-meta.component'
import { formatRelativeTime } from '../utils/format-relative-time'

type HoveredAction = 'accept' | 'reject' | 'comment' | null

type DiffSegment = {
  variant: 'added' | 'removed'
  labelKey: string
  runs: SuggestionTextRun[]
}

const HEADER_KEYS: Record<NonNullable<HoveredAction>, string> = {
  accept: 'EDITOR.TRACK_CHANGES.POPUP_HEADER_ACCEPT',
  reject: 'EDITOR.TRACK_CHANGES.POPUP_HEADER_REJECT',
  comment: 'EDITOR.TRACK_CHANGES.POPUP_HEADER_COMMENT',
}

@Component({
  selector: 'app-suggestion-popup',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslatePipe, SuggestionMetaComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './suggestion-popup.component.html',
  styleUrl: './suggestion-popup.component.scss',
})
export class SuggestionPopupComponent implements OnChanges {
  @Input({ required: true })
  suggestionId!: string

  @Input({ required: true })
  authorName!: string

  @Input()
  createdAt: string | null = null

  @Input({ required: true })
  insertionRuns!: SuggestionTextRun[]

  @Input({ required: true })
  deletionRuns!: SuggestionTextRun[]

  @Input({ required: true })
  formattingAdded!: string[]

  @Input({ required: true })
  formattingRemoved!: string[]

  @Input({ required: true })
  listChanges!: ListChange[]

  @Input({ required: true })
  canResolve!: boolean

  @Output()
  accept = new EventEmitter<string>()

  @Output()
  reject = new EventEmitter<string>()

  @Output()
  comment = new EventEmitter<string>()

  formattingLabels: string[] = []
  listChangeLabels: string[] = []
  category: SuggestionCategory = 'formatting'
  createdAtRelative: string | null = null
  diffSegments: DiffSegment[] = []
  headerTextKey: string = 'EDITOR.TRACK_CHANGES.POPUP_HEADER'
  private hoveredAction: HoveredAction = null

  private translateService = inject(TranslateService)

  ngOnChanges(changes: SimpleChanges) {
    if (changes['insertionRuns'] !== undefined || changes['deletionRuns'] !== undefined) {
      this.category = categorizeSuggestion(this.insertionRuns, this.deletionRuns)
      this.diffSegments = this.buildDiffSegments()
    }
    if (changes['formattingAdded'] !== undefined || changes['formattingRemoved'] !== undefined) {
      this.formattingLabels = collectFormattingLabelDescriptors(this.formattingAdded, this.formattingRemoved).map((descriptor) =>
        this.translateService.instant(
          descriptor.negated ? `EDITOR.TRACK_CHANGES.FORMAT_NOT_${descriptor.key}` : `EDITOR.TRACK_CHANGES.FORMAT_${descriptor.key}`
        )
      )
    }
    if (changes['listChanges'] !== undefined) {
      this.listChangeLabels = collectListChangeLabelDescriptors(this.listChanges).map((descriptor) =>
        this.translateService.instant(`EDITOR.TRACK_CHANGES.${descriptor.key}`, descriptor.params)
      )
    }
    if (changes['createdAt'] !== undefined) {
      this.createdAtRelative = formatRelativeTime(this.createdAt)
    }
    if (changes['canResolve'] !== undefined) {
      this.headerTextKey = this.computeHeaderTextKey()
    }
  }

  setHovered(action: HoveredAction) {
    this.hoveredAction = action
    this.headerTextKey = this.computeHeaderTextKey()
  }

  private computeHeaderTextKey(): string {
    if (this.hoveredAction !== null) {
      return HEADER_KEYS[this.hoveredAction]
    }
    return this.canResolve ? 'EDITOR.TRACK_CHANGES.POPUP_HEADER' : 'EDITOR.TRACK_CHANGES.POPUP_HEADER_COMMENT_ONLY'
  }

  private buildDiffSegments(): DiffSegment[] {
    switch (this.category) {
      case 'replaced':
        return [
          { variant: 'removed', labelKey: 'EDITOR.TRACK_CHANGES.REPLACED', runs: this.deletionRuns },
          { variant: 'added', labelKey: 'EDITOR.TRACK_CHANGES.WITH', runs: this.insertionRuns },
        ]
      case 'added':
        return [{ variant: 'added', labelKey: 'EDITOR.TRACK_CHANGES.ADDED', runs: this.insertionRuns }]
      case 'removed':
        return [{ variant: 'removed', labelKey: 'EDITOR.TRACK_CHANGES.REMOVED', runs: this.deletionRuns }]
      case 'formatting':
        return []
    }
  }

  get hasListChanges(): boolean {
    return this.listChangeLabels.length > 0
  }

  onAccept() {
    this.accept.emit(this.suggestionId)
  }

  onReject() {
    this.reject.emit(this.suggestionId)
  }

  onComment() {
    this.comment.emit(this.suggestionId)
  }
}
